import { FieldLabel, Icon } from '@speaking-coach/ui'
import { formatBusinessDate } from '../common/access-window'
import type { CohortRow } from '../cohorts/types'
import type { ReactNode } from 'react'

function PreviewValue({
  label,
  children,
  wide = false,
}: {
  label: string
  children: ReactNode
  wide?: boolean
}): JSX.Element {
  return (
    <div className={wide ? 'sm:col-span-2' : undefined}>
      <dt className="text-caption text-text-secondary">{label}</dt>
      <dd className="mt-0.5 text-body font-medium">{children}</dd>
    </div>
  )
}

/** One read-only presentation of cohort terms before enrollment creation. */
export function CohortEnrollmentDefaultsPreview({
  cohort,
  includeAccessWindow = true,
  includeLearningContinuity = true,
}: {
  cohort: CohortRow
  /** Creation already has the shared access-window panel beside this preview. */
  includeAccessWindow?: boolean
  /** Batch review owns continuity per student instead of showing one wave-wide value. */
  includeLearningContinuity?: boolean
}): JSX.Element {
  return (
    <section className="rounded-token-card border border-border-default bg-surface-app p-3">
      <FieldLabel
        label="Enrollment defaults"
        help="These cohort values are copied onto the enrollment and locked when it is created."
      />
      <dl className="grid gap-3 sm:grid-cols-2">
        {includeAccessWindow && (
          <PreviewValue label="Access window" wide>
            {formatBusinessDate(cohort.scheduledStartOn, 'Immediately')} →{' '}
            {formatBusinessDate(cohort.accessEndsOn)}
          </PreviewValue>
        )}
        <PreviewValue label="Tier">{cohort.defaultTier}</PreviewValue>
        <PreviewValue label="Program">
          {cohort.defaultProgram.name.en}
        </PreviewValue>
        <PreviewValue label="Trophy goal">
          {cohort.defaultTrophyGoal}
        </PreviewValue>
        <PreviewValue label="Classification">
          {cohort.isTrial ? 'Trial' : 'Standard'}
        </PreviewValue>
        {includeLearningContinuity && (
          <PreviewValue label="Learning continuity" wide>
            <span className="flex flex-wrap gap-x-4 gap-y-1">
              <span className="inline-flex items-center gap-1.5">
                <Icon name="placement" size={14} className="text-text-secondary" />
                New diagnostic
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Icon name="program" size={14} className="text-text-secondary" />
                New curriculum attempt
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Icon name="trophy" size={14} className="text-text-secondary" />
                New trophy progress
              </span>
            </span>
          </PreviewValue>
        )}
      </dl>
    </section>
  )
}
